import React, { useState, useEffect, useContext } from "react"
import { Link } from "gatsby"
import {
  AnimatePresence,
  motion,
  useAnimation,
  useReducedMotion,
} from 'framer-motion'
import {
  Menu,
  MenuButton,
  MenuItems,
  MenuLink,
  MenuPopover,
  useMenuButtonContext,
} from '@reach/menu-button'
import "@reach/menu-button/styles.css"
import { SunIcon } from "../icons/sun-icon"
import { MoonIcon } from "../icons/moon-icon"
import { ThemeContext } from "../../context/themeContext"
import {LinkedInIcon} from "../icons/linkedin-icon"
import {TwitterIcon} from "../icons/twitter-icon"
import {GithubIcon} from "../icons/github-icon"
import {BuymeacoffeeIcon} from "../icons/buymeacoffee-icon"
import {Signature} from "../icons/signature"
import config from "../../../config/website"

const LINKS = [
  { name: "About", to: "/about", label: "View about page" },
  { name: "Project", to: "/project", label: "View project page" },
  { name: "Blog", to: "/blog", label: "View blog page" },
  { name: "Contact", to: "/contact", label: "View contact page" },
]

const MOBILE_LINKS = [{name: 'Home', to: '/', label: "go to home page"}, ...LINKS]

const NavLink = ({ to, label, ...rest }) => {
  return (
    <li className="px-5 py-2">
      <Link
        to={to}
        aria-label={label}
        activeClassName="text-quarternary border-b-2 border-quarternary hover:border-opacity-0"
        className="font-body2 text-lg lg:text-xl font-semibold whitespace-nowrap lg:inline-block border-b-2 border-transparent hover:border-current text-secondary-text hover:text-quarternary dark:hover:border-quarternary cursor-pointer select-none"
        {...rest}
      />
        
    </li>
  )
}

const iconTransformOrigin = {transformOrigin: '50% 100%'}
function DarkModeToggle({variant = 'icon'}) {
  const {theme, setTheme} = useContext(ThemeContext)
  const handleThemeToggle = () => {
    if (theme === "light") {
      setTheme("dark")
    } else {
      setTheme("light")
    }
  }
  return (
    <button
      onClick={handleThemeToggle}
      className={`border-primary hover:border-quarternary focus:border-primary inline-flex items-center justify-center p-1 w-full h-full border-2 rounded-full focus:outline-none overflow-hidden transition
        ${variant === "icon" ? "w-14" 
        : variant === "labelled" ? "px-8" 
        : null}`}
    >
      {/* note that the duration is longer then the one on body, controlling the bg-color */}
      <div className="relative w-8 h-8 hidden lg:block">
        <span
          className="motion-reduce:duration-[0s] origin-[50%_100px] absolute inset-0 text-black dark:text-white transform dark:rotate-0 rotate-90 transition duration-1000"
        >
          <MoonIcon />
        </span>
        <span
          className="motion-reduce:duration-[0s] origin-[50%_100px] absolute inset-0 text-black dark:text-white transform dark:-rotate-90 rotate-0 transition duration-1000"
        >
          <SunIcon />
        </span>
      </div>
      <span className={`text-quarternary font-semibold md:text-2xl font-body2 py-2 ${variant === "icon" && "sr-only"}`}>
        {theme === "dark" ? "switch to light mode" : "switch to dark mode"}
      </span>
    </button>
  )
}

function MobileMenuList() {
  const {isExpanded} = useMenuButtonContext()
  const shouldReduceMotion = useReducedMotion()
  const {theme, setTheme} = useContext(ThemeContext)

  useEffect(() => {
    if (isExpanded) {
      // don't use overflow-hidden, as that toggles the scrollbar and causes layout shift
      document.body.classList.add('fixed')
      document.body.classList.add('overflow-y-scroll')
      // // alternatively, get bounding box of the menu, and set body height to that.
      document.body.style.height = '100vh'
    } else {
      document.body.classList.remove('fixed')
      document.body.classList.remove('overflow-y-scroll')
      document.body.style.removeProperty('height')
    }
  }, [isExpanded])
  return (
    <AnimatePresence>
      {isExpanded ? (
        <MenuPopover
          position={r => ({
            top: `calc(${Number(r?.top) + Number(r?.height)}px + 2.25rem)`, // 2.25 rem = py-9 from navbar
            left: 0,
            bottom: 0,
            right: 0,
          })}
          style={{display: 'block'}}
          className={`z-50`}
        >
          <motion.div
            initial={{y: -50, opacity: 0}}
            animate={{y: 0, opacity: 1}}
            exit={{y: -50, opacity: 0}}
            transition={{
              duration: shouldReduceMotion ? 0 : 0.15,
              ease: 'linear',
            }}
            className={`flex flex-col h-full pb-12 overflow-y-scroll bg-blue-200`}
          >
              <MenuItems className={`p-0 ${theme === "dark" ? "text-color2" : "text-color"}`}>
              {MOBILE_LINKS.map(link => (
                <MenuLink
                  className={`${theme === "dark" ? "border-b" : "border-b2"} border-b border-sky-600`}
                  key={link.to}
                  as={Link}
                  to={link.to}
                  aria-label={link.label}
                >
                  <span className={`flex flex-col items-center justify-center font-semibold md:text-2xl font-body2 py-9 px-10 md:py-16 focus:text-blue-800 active:text-blue-800 border-b border-sky-600`}>
                    {link.name}

                  </span>
                </MenuLink>
              ))}
              <div className={`block text-center py-20 w-9/12 mx-auto `}>
                <DarkModeToggle variant="labelled" />
              </div>
              <div className={`block text-center py-9 w-9/12 mx-auto `}>
                <div className="flex items-center justify-center">
                  <Link to="/" aria-label="go back home" className="text-quarternary">
                    <Signature size={48} title={config.author} />
                  </Link>
                </div>
                <div className="flex flex-col justify-center items-center mt-12">
                  <div className="flex flex-row space-x-6">
                    <a href={config.linkedin} rel="noreferrer noopener" target="_blank" alt={config.linkedin} className="text-quarternary hover:text-blue-500">
                      <LinkedInIcon size={36} />
                    </a>
                    <a href={config.twitter} rel="noreferrer noopener" target="_blank" alt={config.linkedin} className="text-quarternary hover:text-sky-400">
                      <TwitterIcon size={36} />
                    </a>
                    <a href={config.github} rel="noreferrer noopener" target="_blank" alt={config.linkedin} className="text-quarternary hover:text-stone-500">
                      <GithubIcon size={36} />
                    </a>
                    <a href={config.buymeacoffee} rel="noreferrer noopener" target="_blank" alt={config.linkedin} className="text-quarternary hover:text-yellow-300">
                      <BuymeacoffeeIcon size={36}/>
                    </a>
                  </div>
                </div>
              </div>
            </MenuItems>
          </motion.div>
        </MenuPopover>
      ) : null}
    </AnimatePresence>
  )
}

const topVariants = {
  open: {rotate: 45, y: 7},
  closed: {rotate: 0, y: 0},
}

const centerVariants = {
  open: {opacity: 0},
  closed: {opacity: 1},
}

const bottomVariants = {
  open: {rotate: -45, y: -5},
  closed: {rotate: 0, y: 0},
}

function MobileMenu() {
  const shouldReduceMotion = useReducedMotion()
  const transition = shouldReduceMotion ? { duration: 0 } : {}
  return (
    <Menu>
      {({isExpanded}) => {
        const state = isExpanded ? 'open' : 'closed'
        return (
          <>
            <MenuButton className="border-primary hover:border-quarternary focus:border-primary inline-flex items-center justify-center p-3 w-full h-full border-2 rounded-full focus:outline-none overflow-hidden transition">
              <svg
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <motion.rect
                  animate={state}
                  variants={topVariants}
                  transition={transition}
                  x="6"
                  y="9"
                  width="20"
                  height="2"
                  rx="1"
                  fill="currentColor"
                />
                <motion.rect
                  animate={state}
                  variants={centerVariants}
                  transition={transition}
                  x="6"
                  y="15"
                  width="20"
                  height="2"
                  rx="1"
                  fill="currentColor"
                />
                <motion.rect
                  animate={state}
                  variants={bottomVariants}
                  transition={transition}
                  x="6"
                  y="21"
                  width="20"
                  height="2"
                  rx="1"
                  fill="currentColor"
                />
              </svg>
            </MenuButton>
            <div className="text-red-400">
              <MobileMenuList />

            </div>
          </>
        )
      }}
    </Menu>
  )
}

const Navbar = ({
  siteTitle,
  headerLink,
}) => {
  const [openNav, setOpenNav] = useState(false)
  const {theme, setTheme} = useContext(ThemeContext)
  const [offSet, setOffSet] = useState(0)
  const currentUrl = typeof window !== "undefined" ? window.location.pathname : ""

  const toggleNav = () => {
    setOpenNav(!openNav)
  }
  const handleThemeToggle = () => {
    if (theme === "light") {
      setTheme("dark")
    } else {
      setTheme("light")
    }
  }
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.onscroll = () => {
        setOffSet(window.pageYOffset)
      }
    }
  }, [])


  return (
    <nav
      className={`px-5 py-9 lg:py-12 sticky top-0 z-50 ${currentUrl === "/" && "bg-blue-100 dark:bg-gray-900"} ${
        offSet > 1
          && "bg-primary transition duration-500 ease-in-out opacity-90"}`}
    >
      <div className="container mx-auto flex items-center justify-between ">
        <header className="">
          <Link to="/" aria-label="go to home page" className="block text-2xl md:text-3xl lg:text-4xl font-semibold transition text-primary whitespace-nowrap focus:outline-none">
            <h1 className="font-body2 text-transparent bg-clip-text border-b-2 border-transparent hover:border-accent dark:hover:border-quarternary bg-gradient-to-r from-blue-800 via-blue-500 to-gray-900 dark:from-gray-700 dark:via-gray-300 dark:to-white">
              Danniel Hansel
            </h1>
          </Link>
        </header>

        <ul className="hidden lg:flex">
          {LINKS.map(link => (
            <NavLink key={link.to} to={link.to} label={link.label}>
              {link.name}
            </NavLink>
          ))}
        </ul>
        <div className="flex items-center justify-center">
          <div className="block lg:hidden">
            <MobileMenu />
        </div>
        <div className="hidden lg:block">
          <DarkModeToggle variant="icon"/>
        </div>
      </div>
      </div>
    </nav>
  )
}
export default Navbar
